/**
 * HUD panel visibility — persisted in localStorage.
 */
import { useSyncExternalStore } from 'react';

export type HudPreset = 'minimal' | 'balanced' | 'full';

export type HudPreferences = {
  showAiPanel: boolean;
  showObjectives: boolean;
  showNewsFeed: boolean;
  showLayerPanel: boolean;
  showOrgShortcuts: boolean;
  showQuickDebtButton: boolean;
  activePreset: HudPreset | 'custom';
};

const STORAGE_KEY = 'compliance_command_hud_prefs_v2';

export const HUD_PRESETS: Record<HudPreset, HudPreferences> = {
  minimal: {
    showAiPanel: false,
    showObjectives: false,
    showNewsFeed: false,
    showLayerPanel: false,
    showOrgShortcuts: false,
    showQuickDebtButton: false,
    activePreset: 'minimal',
  },
  balanced: {
    showAiPanel: false,
    showObjectives: true,
    showNewsFeed: true,
    showLayerPanel: false,
    showOrgShortcuts: false,
    showQuickDebtButton: true,
    activePreset: 'balanced',
  },
  full: {
    showAiPanel: true,
    showObjectives: true,
    showNewsFeed: true,
    showLayerPanel: true,
    showOrgShortcuts: true,
    showQuickDebtButton: true,
    activePreset: 'full',
  },
};

const DEFAULT_PREFS: HudPreferences = HUD_PRESETS.minimal;

function loadPrefs(): HudPreferences {
  if (typeof localStorage === 'undefined') return DEFAULT_PREFS;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_PREFS;
    const parsed = JSON.parse(raw) as Partial<HudPreferences>;
    return { ...DEFAULT_PREFS, ...parsed };
  } catch {
    return DEFAULT_PREFS;
  }
}

let state: HudPreferences = loadPrefs();
const listeners = new Set<() => void>();

function persist() {
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }
}

function emit() {
  listeners.forEach((l) => l());
}

export const hudPreferencesStore = {
  getState: () => state,
  setPreference: <K extends keyof HudPreferences>(key: K, value: HudPreferences[K]) => {
    state = { ...state, [key]: value, activePreset: 'custom' };
    persist();
    emit();
  },
  setPreferences: (prefs: Partial<HudPreferences>) => {
    state = { ...state, ...prefs };
    persist();
    emit();
  },
  applyPreset: (preset: HudPreset) => {
    state = { ...HUD_PRESETS[preset] };
    persist();
    emit();
  },
  subscribe: (listener: () => void) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
};

export function useHudPreferences(): HudPreferences {
  return useSyncExternalStore(
    hudPreferencesStore.subscribe,
    hudPreferencesStore.getState,
    hudPreferencesStore.getState
  );
}